import { IUser } from '@gig/magic-sdk/dist/adapter/users/user';
import { Language } from '@gig/magic-sdk/dist/adapter/users/language';
import {
    ISmarticoSdk,
    ISmarticoUserProfile,
    ISmarticoVipLevel,
    SmarticoIdentifyHandler,
    SmarticoPropsChangeHandler,
    SmarticoVipLevelListener,
} from './smartico.interface';

const SDK_RETRY_INITIAL_INTERVAL_MS = 250;
const SDK_RETRY_MAX_INTERVAL_MS = 5000;
const WIDGET_READY_POLL_INTERVAL_MS = 100;
const WIDGET_READY_TIMEOUT_MS = 5000;
const VIP_PROFILE_FIELDS: Array<keyof ISmarticoUserProfile> = [
    'ach_level_current',
    'ach_level_current_id',
    'ach_points_ever',
    'ach_gamification_in_control_group',
];

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        _smartico_user_id: string | null;
        _smartico_language: string | null;
        _smartico?: ISmarticoSdk;
    }
}

export class SmarticoService {
    /**
     * This method is responsible for setting the user's ID and language
     * on the global window object for the Smartico script to read.
     */
    public setUserData(user: IUser | null, lang: Language | null) {
        window._smartico_user_id = user ? String(user.id) : null;
        window._smartico_language = user ? lang : null;
    }

    /**
     * This method opens the Smartico gamification widget.
     * It should be called when the user clicks the corresponding menu item.
     */
    public async openGamificationWidget(): Promise<boolean> {
        const sdk = await this.waitForIdentifiedSdk();

        if (!sdk) {
            console.warn('Smartico SDK is not ready or the user is not identified.');
            return false;
        }

        try {
            sdk.dp('dp:gf');
            return true;
        } catch {
            console.warn('Smartico widget could not be opened.');
            return false;
        }
    }

    /**
     * Subscribes to the current Smartico VIP level and keeps it in sync with
     * Smartico's server-driven profile updates.
     */
    public subscribeToVipLevel(listener: SmarticoVipLevelListener): () => void {
        let sdk: ISmarticoSdk | undefined;
        let retryTimer: number | undefined;
        let retryDelay = SDK_RETRY_INITIAL_INTERVAL_MS;
        let refreshId = 0;
        let disposed = false;

        const emitCurrentLevel = async () => {
            if (!sdk || disposed) {
                return;
            }

            const currentRefreshId = ++refreshId;

            try {
                const profile = sdk.api.getUserProfile();

                if (
                    profile.ach_gamification_in_control_group !== false ||
                    !this.hasLevel(profile)
                ) {
                    if (!disposed && currentRefreshId === refreshId) {
                        listener(null);
                    }
                    return;
                }

                const currentLevel = await sdk.api.getCurrentLevel();
                const level = this.normalizeVipLevel(currentLevel);

                if (!disposed && currentRefreshId === refreshId) {
                    listener(level);
                }
            } catch {
                if (!disposed && currentRefreshId === refreshId) {
                    listener(null);
                }
            }
        };

        const onIdentify: SmarticoIdentifyHandler = (errorCode) => {
            if (disposed) {
                return;
            }

            if (errorCode !== 0) {
                refreshId += 1;
                listener(null);
                return;
            }

            void emitCurrentLevel();
        };

        const onPropsChange: SmarticoPropsChangeHandler = (properties) => {
            if (disposed) {
                return;
            }

            const hasVipLevelChange = VIP_PROFILE_FIELDS.some((field) =>
                Object.prototype.hasOwnProperty.call(properties, field),
            );

            if (hasVipLevelChange) {
                void emitCurrentLevel();
            }
        };

        const attach = () => {
            if (disposed) {
                return;
            }

            if (!this.isSdkReady(window._smartico)) {
                retryTimer = window.setTimeout(attach, retryDelay);
                retryDelay = Math.min(
                    retryDelay * 2,
                    SDK_RETRY_MAX_INTERVAL_MS,
                );
                return;
            }

            sdk = window._smartico;
            sdk.on('identify', onIdentify);
            sdk.on('props_change', onPropsChange);
            void emitCurrentLevel();
        };

        attach();

        return () => {
            disposed = true;
            refreshId += 1;

            if (retryTimer !== undefined) {
                window.clearTimeout(retryTimer);
            }

            if (sdk) {
                sdk.off('identify', onIdentify);
                sdk.off('props_change', onPropsChange);
            }
        };
    }

    private isSdkReady(sdk: ISmarticoSdk | undefined): sdk is ISmarticoSdk {
        return !!(
            sdk?.on &&
            sdk.off &&
            sdk.api?.getUserProfile &&
            sdk.api.getCurrentLevel
        );
    }

    private waitForIdentifiedSdk(): Promise<ISmarticoSdk | null> {
        return new Promise((resolve) => {
            let elapsed = 0;

            const check = () => {
                const sdk = window._smartico;

                try {
                    if (
                        typeof sdk?.dp === 'function' &&
                        typeof sdk.checkSuccessfullyIdentify === 'function' &&
                        sdk.checkSuccessfullyIdentify()
                    ) {
                        resolve(sdk);
                        return;
                    }
                } catch {
                    // Keep waiting while Smartico completes its initialization.
                }

                if (elapsed >= WIDGET_READY_TIMEOUT_MS) {
                    resolve(null);
                    return;
                }

                elapsed += WIDGET_READY_POLL_INTERVAL_MS;
                window.setTimeout(check, WIDGET_READY_POLL_INTERVAL_MS);
            };

            check();
        });
    }

    private hasLevel(profile: ISmarticoUserProfile): boolean {
        return (
            profile.ach_level_current_id !== undefined ||
            !!profile.ach_level_current?.trim()
        );
    }

    private normalizeVipLevel(
        level: ISmarticoVipLevel,
    ): ISmarticoVipLevel | null {
        const name = level.name?.trim();
        const progress = Number(level.progress);

        if (!name || !Number.isFinite(progress)) {
            return null;
        }

        return {
            name,
            progress: Math.min(100, Math.max(0, progress)),
        };
    }
}

export const smarticoService = new SmarticoService();
